import React, { PropsWithRef, useMemo, useState } from 'react';
import { INPUT_TYPE, InputFileSearchProps } from './types';
import { Typography } from '@shared/ui/typography/typography';
import {
    FilesList,
    FindInputPlaceholder,
    FindInputWrapper,
    InputBody,
    StyledFormGroup,
} from './input-file-search.styles';
import { DEFAULT_INPUT_FILE_VALUE, DEFAULT_PARAMETERS, INPUT_TYPE_RADIOS, SEARCH_OPTIONS_VALUE } from './constants';
import { File } from '@shared/ui/file/file';
import { RadioGroup } from '@shared/ui/radio-group/radio-group';
import { InputDropZone } from '@shared/ui/input-drop-zone/input-drop-zone';
import { Select } from '@shared/ui/select/select';
import { FileProps } from '@shared/ui/file/types';
import { ErrorMessageInput } from '@shared/ui/error-message-input/error-message-input';
import { useFileSelect } from './hooks/use-file-options/use-file-options';
import { useHandlers } from './hooks/use-handlers/use-handlers';
import { useFilesInfinityListQuery } from './api/file-api';
import { FilesListDataRequest } from '@shared/api/file/file-types';

export const InputFileSearch = ({
    error,
    label,
    accept,
    hint,
    name,
    value = DEFAULT_INPUT_FILE_VALUE,
    placeholderLocalInput,
    placeholderSearchInput,
    required,
    max,
    disabled,
    selectLabel = 'Прикрепление файлов',
    selectPlaceholder = 'Выберите файлы из списка',
    onChange,
}: PropsWithRef<InputFileSearchProps>) => {
    const [inputTypeFields, setInputTypeFields] = useState(INPUT_TYPE_RADIOS);
    const [isShowSearchPlaceholder, setIsShowSearchPlaceholder] = useState(true);

    /* Products */
    const [filesListParameters, setFilesListParameters] = useState<FilesListDataRequest>(DEFAULT_PARAMETERS);
    const {
        data: filesList,
        isLoading: isFilesListLoading,
        isFetching: isFilesListFetching,
    } = useFilesInfinityListQuery(filesListParameters);

    const { filesOptions, isShowEmptyLoaderFliles, infinityScrollFilesProps, filteredFileData, onSearchFiles } =
        useFileSelect({
            accept,
            value,
            fileData: filesList,
            isLoading: isFilesListLoading,
            isFetching: isFilesListFetching,
            setFilesListParameters,
        });

    const {
        handleChangeInputType,
        handleAddLocalFiles,
        handleAddUploadedFile,
        handleRemoveInitialUploadedFile,
        handleRemoveAddedUploadedFile,
        handleRemoveLocalFile,
    } = useHandlers({
        value,
        filteredFileData,
        max,
        inputTypeFields,
        setInputTypeFields,
        setIsShowSearchPlaceholder,
        onChange,
    });

    const activeInputTypeField = useMemo(() => {
        return inputTypeFields.find((field) => field.checked) || INPUT_TYPE_RADIOS[0];
    }, [inputTypeFields]);

    const isPossibleLoadMore = max
        ? value.localFiles.length +
              value.initialUploadedFiles.length +
              value.addedUploadedFiles.length -
              value.deletedUploadedFiles.length <
          max
        : true;

    /* File list props */

    const filesListProps: (FileProps & { key: React.Key })[] = useMemo(() => {
        const initialUploadedIds = value.initialUploadedFiles.map((file) => file.id);
        const addedUploadedIds = value.addedUploadedFiles.map((file) => file.id);
        const localFiles = value.localFiles.map((file) => file.file);
        const props = value.filesQueue.map((file) => {
            if ('url' in file && initialUploadedIds.includes(Number(file.id))) {
                return {
                    key: file.url,
                    uploadedFile: file,
                    error: file.error,
                    isLoading: file.isLoading,
                    disabled: file.disabled,
                    onRemove: handleRemoveInitialUploadedFile(file),
                };
            }
            if ('url' in file && addedUploadedIds.includes(Number(file.id))) {
                return {
                    key: file.url,
                    uploadedFile: file,
                    error: file.error,
                    isLoading: file.isLoading,
                    disabled: file.disabled,
                    onRemove: handleRemoveAddedUploadedFile(file),
                };
            }
            if ('file' in file && localFiles.includes(file.file)) {
                return {
                    key: `${file.file.name}${file.base64.slice(0, 20)}`,
                    localFile: file,
                    error: file.error,
                    isLoading: file.isLoading,
                    disabled: file.disabled,
                    onRemove: handleRemoveLocalFile(file),
                };
            }
        });
        return props.filter(Boolean) as (FileProps & { key: React.Key })[];
    }, [value]);

    return (
        <StyledFormGroup
            label={label}
            required={required}
            $error={error}
            errorMessage={<ErrorMessageInput error={error} hint={hint} />}>
            <InputBody>
                <RadioGroup
                    name={`radio-type-file_${name}`}
                    fields={inputTypeFields}
                    onChange={handleChangeInputType}
                />
                <FilesList>
                    {activeInputTypeField.value === INPUT_TYPE.Upload && isPossibleLoadMore && (
                        <InputDropZone
                            accept={accept}
                            placeholder={placeholderLocalInput}
                            disabled={disabled}
                            max={max}
                            onChange={handleAddLocalFiles}
                        />
                    )}
                    {activeInputTypeField.value === INPUT_TYPE.Search && isPossibleLoadMore && (
                        <FindInputWrapper>
                            <Select
                                isAsync
                                label={selectLabel}
                                placeholder={selectPlaceholder}
                                isShowPreloaderInList={isShowEmptyLoaderFliles}
                                infinityScrollProps={infinityScrollFilesProps}
                                values={SEARCH_OPTIONS_VALUE}
                                options={filesOptions}
                                onSearch={onSearchFiles}
                                onChange={handleAddUploadedFile}
                            />
                            {isShowSearchPlaceholder && (
                                <FindInputPlaceholder>
                                    <Typography align='center'>
                                        {placeholderSearchInput ||
                                            `Для начала поиска начните вводить название файла.\nЗатем выберите нужное из выпадающего списка`}
                                    </Typography>
                                </FindInputPlaceholder>
                            )}
                        </FindInputWrapper>
                    )}
                    {filesListProps.map((fileProps) => (
                        <File {...fileProps} key={fileProps.key} />
                    ))}
                </FilesList>
            </InputBody>
        </StyledFormGroup>
    );
};

InputFileSearch.displayName = 'InputFileSearch';
